import { BLOCK_CAPS, WORLD_PACKAGE_LIMITS } from './format.js';
import { COLOUR, ID, UNSAFE_TEXT, dutch, isNumber, isRecord, problem, show } from './util.js';
const E = WORLD_PACKAGE_LIMITS.extent;
const num = (min, max, integer = false) => (value, path) => {
    if (!isNumber(value))
        return [problem(path, 'moet een getal zijn')];
    if (integer && !Number.isInteger(value))
        return [problem(path, 'moet een geheel getal zijn')];
    return value < min || value > max
        ? [problem(path, `moet tussen ${dutch(min)} en ${dutch(max)} liggen, niet ${dutch(value)}`)]
        : [];
};
const nullable = (check) => (value, path) => value === null ? [] : check(value, path);
const colour = (value, path) => typeof value === 'string' && COLOUR.test(value)
    ? []
    : [problem(path, 'moet een kleur zijn als "#a1b2c3"')];
export const isPoint2 = (v) => Array.isArray(v) &&
    v.length === 2 &&
    v.every(isNumber) &&
    Math.abs(v[0]) <= E &&
    Math.abs(v[1]) <= E;
export const isPoint3 = (v) => Array.isArray(v) &&
    v.length === 3 &&
    v.every(isNumber) &&
    Math.abs(v[0]) <= E &&
    Math.abs(v[2]) <= E &&
    v[1] >= -500 &&
    v[1] <= 2000;
const point2 = (v, p) => (isPoint2(v) ? [] : [problem(p, 'moet een punt [x, z] zijn')]);
const point3 = (v, p) => (isPoint3(v) ? [] : [problem(p, 'moet een punt [x, y, z] zijn')]);
const span = (v, p) => isPoint2(v) && v[0] < v[1] ? [] : [problem(p, 'moet [van, tot] zijn met van < tot')];
const polygon = (v, p) => Array.isArray(v) && v.length >= 3 && v.length <= 32 && v.every(isPoint2)
    ? []
    : [problem(p, 'moet 3 tot 32 punten [x, z] hebben')];
const id = (v, p) => typeof v === 'string' && ID.test(v)
    ? []
    : [problem(p, 'moet een naam zijn als "hal" (kleine letters, cijfers, -)')];
const label = (v, p) => typeof v === 'string' && v.trim().length >= 1 && v.length <= 20 && !UNSAFE_TEXT.test(v)
    ? []
    : [problem(p, 'moet een tekst van 1 tot 20 tekens zijn')];
function record(value, path, fields) {
    const names = Object.keys(fields);
    if (!isRecord(value))
        return [problem(path, `moet {${names.join(', ')}} zijn`)];
    const out = [];
    for (const name of names) {
        if (!Object.hasOwn(value, name) || value[name] === undefined)
            out.push(problem(`${path}/${name}`, 'ontbreekt'));
        else
            out.push(...fields[name](value[name], `${path}/${name}`));
    }
    for (const name of Object.keys(value))
        if (!Object.hasOwn(fields, name))
            out.push(problem(`${path}/${show(name)}`, 'onbekend veld'));
    return out;
}
function list(value, path, min, max, what, fields) {
    if (!Array.isArray(value) || value.length < min || value.length > max)
        return [problem(path, `moet een lijst van ${min} tot ${max} ${what} zijn`)];
    return value.flatMap((item, i) => record(item, `${path}/${i}`, fields));
}
const LAMP = {
    at: point3,
    colour,
    intensity: num(0, 200),
    range: num(0.5, 100),
    aim: nullable(point3),
    angle: num(1, 90),
    halo: num(0, 20),
    flicker: num(0, 1),
};
/** Lamps: at most 24, of which at most BLOCK_CAPS.lights light (the rest are halos only). */
export function lampsProblems(value, path) {
    const out = list(value, path, 0, 24, 'lampen', LAMP);
    // A spot that aims at its own place has no direction.
    if (out.length === 0 && Array.isArray(value))
        value.forEach((lamp, i) => {
            if (lamp.aim &&
                Math.hypot(...lamp.aim.map((v, k) => v - lamp.at[k])) < 0.1)
                out.push(problem(`${path}/${i}/aim`, 'moet minstens 0,1 m van at liggen'));
        });
    const lit = Array.isArray(value)
        ? value.filter((lamp) => isRecord(lamp) && isNumber(lamp['intensity']) && lamp['intensity'] > 0)
            .length
        : 0;
    if (lit > BLOCK_CAPS.lights) {
        out.push(problem(path, `hooguit ${BLOCK_CAPS.lights} lampen die echt licht geven (intensity > 0); de rest alleen een gloed (intensity 0)`));
    }
    return out;
}
const SIZE = nullable(num(0.1, 4));
const WALL = {
    from: point2,
    to: point2,
    low: num(0, 10),
    high: num(0.1, 20),
    maxSize: SIZE,
};
/** One of the plaatsruimte's compound settings. */
export function placementProblems(kind, value, path) {
    switch (kind) {
        case 'rooms':
            return list(value, path, 1, 6, 'kamers', {
                id,
                name: label,
                area: polygon,
                view: (v, p) => record(v, p, { pos: point3, look: point3 }),
            });
        case 'floors':
            return list(value, path, 1, 16, 'vloeren', { area: polygon, maxSize: SIZE });
        case 'tables':
            return list(value, path, 0, 16, 'tafels', {
                area: polygon,
                height: num(0.2, 3),
                maxSize: SIZE,
            });
        case 'walls': {
            const out = list(value, path, 0, 32, 'wanden', WALL);
            if (out.length > 0 || !Array.isArray(value))
                return out;
            value.forEach((wall, i) => {
                if (wall.high <= wall.low)
                    out.push(problem(`${path}/${i}/high`, 'moet hoger zijn dan low'));
                if (Math.hypot(wall.to[0] - wall.from[0], wall.to[1] - wall.from[1]) < 0.1)
                    out.push(problem(`${path}/${i}/to`, 'moet minstens 0,1 m van from liggen'));
            });
            return out;
        }
        case 'start': {
            const out = record(value, path, {
                areas: (v, p) => list(v, p, 1, 4, 'rechthoeken', { x: span, z: span }),
                from: nullable(num(-E, E)),
                turn: num(0, 359, true),
            });
            // The packing lays pieces out along the axes: only quarter turns have a footprint it can read.
            if (out.length === 0 && isRecord(value) && value['turn'] % 90 !== 0)
                out.push(problem(`${path}/turn`, 'moet 0, 90, 180 of 270 zijn'));
            return out;
        }
        case 'photos':
            return list(value, path, 0, 4, "foto's", { eye: point3, look: point3, fov: num(20, 110) });
    }
}
//# sourceMappingURL=shapes.js.map